import messages from '../constants/logMessages.json';
import { Meta } from '../types/index';
import logging, { getMessage } from '../utils/logging';
import { getQbeTrack, storageKey } from './config';

const { errorHeading, errorBold } = messages;

const metaKey = () => `__qbe_analytics_meta_${storageKey}`;

// Initialize meta object
let meta: { [key: string]: any } = {};
// Initialize meta collection array
let metaCollectionArray: string[] = [];

/**
 * Clear stored meta from sessionStorage
 */
export const clearMeta = () => {
  sessionStorage.removeItem(metaKey());
};

/**
 * Set meta collection with given meta keys array
 * @param {string[]} metaKeysArray
 */
export const setMetaCollection = (metaKeysArray: string[]) => {
  metaCollectionArray = [...metaCollectionArray, ...metaKeysArray];
  return metaCollectionArray;
};

/**
 * Get the current meta collection
 */
export const getMetaCollection = () => metaCollectionArray;

/**
 * Get the stored meta from sessionStorage
 */
export const getStoredMeta = (): { [key: string]: any } => {
  const storedMeta = window.sessionStorage.getItem(metaKey());
  return storedMeta ? JSON.parse(storedMeta) : {};
};

/**
 * Get the merged meta object (stored + in-memory meta)
 */
export const getMergedMeta = () => ({
  ...getStoredMeta(),
  ...meta,
});

/**
 * Collect and store new meta data
 * @param {Object} data
 */
export const collectMeta = (data: { [key: string]: any }) => {
  meta = {
    ...getStoredMeta(),
    ...meta,
    ...data,
  };

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(metaKey(), JSON.stringify(meta));
  }

  return meta;
};

/**
 * Collect and store API meta data
 * @param {Object} data
 */
export const collectApiMeta = (data: { [key: string]: any } | null) => {
  if (!data) return getMergedMeta();

  const metaData = getMetaCollection().reduce(
    (obj: { [key: string]: any }, k: string) => ({
      ...obj,
      [k]: data[k],
    }),
    {}
  );

  meta = {
    ...metaData,
    ...getStoredMeta(),
  };

  if (typeof window !== 'undefined') {
    sessionStorage.setItem(metaKey(), JSON.stringify(meta));
  }

  return meta;
};

/**
 * Check and retrieve specific meta keys direct from the window object
 * @param Array<string> metaDataKeys
 */
export const getPartialMetaData = (
  metaDataKeys: Array<string>
): Partial<Meta> | false => {
  const qbeTrack = getQbeTrack();
  if (!qbeTrack || !qbeTrack.meta) {
    logging.error(getMessage('qbeTrackNotFound'), errorHeading, errorBold);
    return false;
  }
  const reduceMeta = (keys: Array<string>, metaSource: Meta): Partial<Meta> => {
    const partial: Record<string, Meta[keyof Meta]> = {};
    keys.forEach((key) => {
      if (key in metaSource) {
        const metaKey = key as keyof Meta;
        partial[metaKey as string] = metaSource[metaKey];
      }
    });
    return partial as Partial<Meta>;
  };

  return reduceMeta(metaDataKeys, qbeTrack.meta);
};
